import { useAuth, UserRole } from "@/features/auth";
import { ReactNode } from "react";

interface RoleGateProps {
    children: ReactNode;
    roles?: UserRole[];
    fallback?: ReactNode;
    requireAll?: boolean;
}

/**
 * Conditionally renders children depending on the current user's roles.
 */
export function RoleGate({ children, roles, fallback = null, requireAll = false }: Readonly<RoleGateProps>) {
    const { isLoading, hasRole } = useAuth();

    // While auth is loading, avoid flashing fallback; render nothing
    if (isLoading) return null;

    if (!roles || roles.length === 0) return <>{children}</>;

    if (rolesMatch(hasRole, roles, requireAll)) return <>{children}</>;

    return <>{fallback}</>;
}

interface RoleSwitchCase {
    roles?: UserRole[];
    children: ReactNode;
    requireAll?: boolean;
}

interface RoleSwitchProps {
    cases: RoleSwitchCase[];
    fallback?: ReactNode;
    usePreview?: boolean;
}

/**
 * Renders the first matching case based on user roles.
 * Array order defines priority.
 */
export function RoleSwitch({ cases, fallback = null }: Readonly<RoleSwitchProps>) {
    const { isLoading, hasRole } = useAuth();

    // While auth is loading, avoid flashing fallback; render nothing
    if (isLoading) return null;

    for (const roleCase of cases) {
        const { roles, children, requireAll = false } = roleCase;
        if (!roles || roles.length === 0) return <>{children}</>;
        if (rolesMatch(hasRole, roles, requireAll)) return <>{children}</>;
    }

    return <>{fallback}</>;
}

/**
 * Determine if the current user matches the provided role requirements.
 * - If roles are undefined/empty, returns false (restriction is required).
 * - When requireAll is true, all roles must be present; otherwise any role suffices.
 */
function rolesMatch(check: (role: UserRole) => boolean, roles?: UserRole[], requireAll: boolean = false): boolean {
    if (!roles || roles.length === 0) return false;
    const checks = roles.map((role) => check(role));
    return requireAll ? checks.every(Boolean) : checks.some(Boolean);
}
